package one.devsbox.www.ui.widgets

import androidx.compose.animation.core.*
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Outline
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.ArrowLeft
import compose.assets.svg.CloseOutline
import compose.assets.svg.ResetOutline
import one.devsbox.www.www_portal.generated.resources.Res
import one.devsbox.www.www_portal.generated.resources.ui_devices_edit_deviceeui_cancel
import org.jetbrains.compose.resources.stringResource
import kotlin.math.abs

@Composable
fun FullscreenErrorUI(
    message: String,
    onBack: () -> Unit,
    onReload: () -> Unit,
    color: Color = MaterialTheme.colorScheme.error
) {
    val lineFraction = remember { Animatable(1f) }
    var isRevealed by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        // at start reveal effect: horizontal line going bottom (1f) to top (0f) in 500ms
        lineFraction.animateTo(
            targetValue = 0f,
            animationSpec = spring(stiffness = Spring.StiffnessVeryLow)
        )
        isRevealed = true

        // then starts repeating from bottom to top on a timer of 3000ms
        while (true) {
            lineFraction.animateTo(
                targetValue = 1f,
                animationSpec = tween(durationMillis = 3000, easing = LinearEasing)
            )
            lineFraction.animateTo(
                targetValue = 0f,
                animationSpec = tween(durationMillis = 3000, easing = LinearEasing)
            )
        }
    }

    val yOffsetFraction = lineFraction.value
    val backgroundColor = MaterialTheme.colorScheme.background
    val scanlineColor = color

    val revealShape = remember(yOffsetFraction, isRevealed) {
        object : Shape {
            override fun createOutline(
                size: Size,
                layoutDirection: LayoutDirection,
                density: Density
            ): Outline {
                return if (isRevealed) {
                    Outline.Rectangle(Rect(0f, 0f, size.width, size.height))
                } else {
                    val yPos = size.height * yOffsetFraction
                    Outline.Rectangle(Rect(0f, yPos, size.width, size.height))
                }
            }
        }
    }

    Box(modifier = Modifier.fillMaxSize().background(backgroundColor)) {
        // 1. Cybernetic Red Scanning Grid Background
        Canvas(modifier = Modifier.fillMaxSize()) {
            val canvasWidth = size.width
            val canvasHeight = size.height
            val yPos = canvasHeight * yOffsetFraction
            val gridSpacing = 40.dp.toPx()

            // Vertical Grid Lines
            val numCols = (canvasWidth / gridSpacing).toInt()
            for (col in 0..numCols) {
                val x = col * gridSpacing
                val startY = if (isRevealed) 0f else yPos
                drawLine(
                    color = scanlineColor.copy(alpha = 0.08f),
                    start = Offset(x, startY),
                    end = Offset(x, canvasHeight),
                    strokeWidth = 1f
                )
            }

            // Horizontal Grid Lines with reactive sweep glow
            val numRows = (canvasHeight / gridSpacing).toInt()
            for (row in 0..numRows) {
                val y = row * gridSpacing
                if (!isRevealed && y < yPos) continue

                val distance = abs(y - yPos)
                val highlightAlpha = (1f - (distance / 150.dp.toPx()).coerceIn(0f, 1f)) * 0.14f
                val rowColor = scanlineColor.copy(alpha = 0.02f + highlightAlpha)
                drawLine(
                    color = rowColor,
                    start = Offset(0f, y),
                    end = Offset(canvasWidth, y),
                    strokeWidth = if (highlightAlpha > 0.02f) 1.5f else 1f
                )
            }

            // 2. Glowing Laser Sweeping Scanline

            // Layer A: Wide Ambient Glow
            val glowHeightA = 120.dp.toPx()
            val brushA = Brush.verticalGradient(
                colors = listOf(
                    Color.Transparent,
                    scanlineColor.copy(alpha = 0.03f),
                    scanlineColor.copy(alpha = 0.03f),
                    Color.Transparent
                ),
                startY = yPos - glowHeightA,
                endY = yPos + glowHeightA
            )
            drawRect(
                brush = brushA,
                topLeft = Offset(0f, yPos - glowHeightA),
                size = Size(canvasWidth, glowHeightA * 2)
            )

            // Layer B: Medium Glow
            val glowHeightB = 40.dp.toPx()
            val brushB = Brush.verticalGradient(
                colors = listOf(
                    Color.Transparent,
                    scanlineColor.copy(alpha = 0.15f),
                    scanlineColor.copy(alpha = 0.15f),
                    Color.Transparent
                ),
                startY = yPos - glowHeightB,
                endY = yPos + glowHeightB
            )
            drawRect(
                brush = brushB,
                topLeft = Offset(0f, yPos - glowHeightB),
                size = Size(canvasWidth, glowHeightB * 2)
            )

            // Layer C: Intense Core Glow
            val glowHeightC = 12.dp.toPx()
            val brushC = Brush.verticalGradient(
                colors = listOf(
                    Color.Transparent,
                    scanlineColor.copy(alpha = 0.4f),
                    scanlineColor.copy(alpha = 0.4f),
                    Color.Transparent
                ),
                startY = yPos - glowHeightC,
                endY = yPos + glowHeightC
            )
            drawRect(
                brush = brushC,
                topLeft = Offset(0f, yPos - glowHeightC),
                size = Size(canvasWidth, glowHeightC * 2)
            )

            // 3. Margin Laser Calibration Indicators
            // Left Dot
            drawCircle(
                color = scanlineColor.copy(alpha = 0.2f),
                radius = 8.dp.toPx(),
                center = Offset(4.dp.toPx(), yPos)
            )
            drawCircle(
                color = scanlineColor,
                radius = 3.dp.toPx(),
                center = Offset(4.dp.toPx(), yPos)
            )

            // Right Dot
            drawCircle(
                color = scanlineColor.copy(alpha = 0.2f),
                radius = 8.dp.toPx(),
                center = Offset(canvasWidth - 4.dp.toPx(), yPos)
            )
            drawCircle(
                color = scanlineColor,
                radius = 3.dp.toPx(),
                center = Offset(canvasWidth - 4.dp.toPx(), yPos)
            )

            // 4. Razor Sharp Core laser Edge
            drawLine(
                color = scanlineColor,
                start = Offset(0f, yPos),
                end = Offset(canvasWidth, yPos),
                strokeWidth = 1.5.dp.toPx()
            )
        }

        // Center Content Overlay (Error Status & Controls)
        Column(
            modifier = Modifier
                .fillMaxSize()
                .clip(revealShape)
                .padding(32.dp),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // High-Tech Cybernetic Cross Icon
            Box(
                modifier = Modifier
                    .size(80.dp)
                    .border(1.5.dp, scanlineColor, RoundedCornerShape(4.dp))
                    .background(scanlineColor.copy(alpha = 0.05f)),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = Svg.CloseOutline,
                    contentDescription = null,
                    tint = scanlineColor,
                    modifier = Modifier.size(40.dp)
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            // Warning Title
            Text(
                text = "SYSTEM ERROR",
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.Bold,
                color = scanlineColor,
                letterSpacing = 2.sp
            )

            Spacer(modifier = Modifier.height(12.dp))

            // Message Container
            Text(
                text = message.uppercase(),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center,
                modifier = Modifier
                    .fillMaxWidth(0.8f)
                    .padding(horizontal = 16.dp)
            )

            Spacer(modifier = Modifier.height(40.dp))

            // Interaction Controls
            Row(
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Outlined Back Button
                OutlinedButton(
                    onClick = onBack,
                    shape = RoundedCornerShape(4.dp),
                    border = BorderStroke(width = 0.5.dp, color = MaterialTheme.colorScheme.outlineVariant),
                    colors = ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.onSurfaceVariant)
                ) {
                    Icon(
                        imageVector = Svg.ArrowLeft,
                        contentDescription = null,
                        modifier = Modifier.size(16.dp)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = stringResource(Res.string.ui_devices_edit_deviceeui_cancel),
                        style = MaterialTheme.typography.labelLarge,
                        fontWeight = FontWeight.Bold
                    )
                }

                // Cybernetic Warning Reload Button
                Button(
                    onClick = onReload,
                    shape = RoundedCornerShape(4.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = scanlineColor,
                        contentColor = MaterialTheme.colorScheme.onError
                    )
                ) {
                    Icon(
                        imageVector = Svg.ResetOutline,
                        contentDescription = null,
                        modifier = Modifier.size(16.dp)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = "RETRY",
                        style = MaterialTheme.typography.labelLarge,
                        fontWeight = FontWeight.Bold
                    )
                }
            }
        }
    }
}
